import { StateService } from '@uirouter/angular';
import { Injectable } from '@angular/core';
import * as _ from 'lodash';
import { UserService } from '../../../core/services/core.user.service';
import { UiService } from '../../../core/services/core.ui.service';
import { TrackerService } from '../../../plugins/tracker/services/tracker.service';
import { TrackeritemRest } from '../../../plugins/tracker/services/trackeritem.rest';
import { TagsModel } from '../../../core/services/core.models.tags.component';
import { NotificationService } from '../../../core/services/core.notification.service';
import { FrsReleaseService } from '../../../plugins/frs/services/frs.release.service';

@Injectable()
export class TrackeritemService {

    public errorMessages; // Error messages to show in UI
    public trackeritem;
    public tempTid;

    private followupDrafts: Map<string, string> = new Map();

    /**
     * Retrieves the saved follow-up draft for a given tracker item.
     * @param trackerId The tracker ID
     * @param itemId The tracker item ID
     * @returns The draft text, or an empty string if no draft exists
     */
    public getFollowupDraft(trackerId: number, itemId: number): string {
        return this.followupDrafts.get(`${trackerId}-${itemId}`) || '';
    }

    /**
     * Saves or removes a follow-up draft for a given tracker item.
     * If the draft is empty/falsy, the stored draft is removed.
     * @param trackerId The tracker ID
     * @param itemId The tracker item ID
     * @param draft The draft text to save
     */
    public setFollowupDraft(trackerId: number, itemId: number, draft: string): void {
        if (draft) {
            this.followupDrafts.set(`${trackerId}-${itemId}`, draft);
        } else {
            this.followupDrafts.delete(`${trackerId}-${itemId}`);
        }
    }

    /**
     * Removes the stored follow-up draft for a given tracker item.
     * @param trackerId The tracker ID
     * @param itemId The tracker item ID
     */
    public clearFollowupDraft(trackerId: number, itemId: number): void {
        this.followupDrafts.delete(`${trackerId}-${itemId}`);
    }

    constructor(private trackeritemRest: TrackeritemRest, private trackerService: TrackerService,
        private uiService: UiService, private tagsModel: TagsModel, private userService: UserService,
        private notificationService: NotificationService, private frsReleaseService: FrsReleaseService,
        private stateService: StateService
    ) {
        this.errorMessages = {
            length      : 'Bad length',
            length_args: ' (valid range is $1 to $2)',
            required    : 'Required field',
            in          : 'Invalid value',
            invalid_element : 'Invalid value',
            bad_summary : 'Summary is required',
            bad_priority: 'Select a priority',
            bad_details : 'Details field is required',
            permission: 'You don\'t have permission to set this value'
        };
    }

    //Returns 'project' sub-namespace
    get constants() {
        var _self = this;

        function getProxy(_self) {
            return (...args) => { return _self.getProjects(...args) };
        }

        //Returns an object with closures for each method, 
        //to preserve the lexical scope of the instance
        return {
            get: getProxy(_self)
        };
    }

    private setDefaultForExtraField(_ti_model, extra_fields, field_name, field_value) {
        if (_ti_model.extraFields.hasOwnProperty(field_name)) {
            _ti_model.extraFields[field_name].fieldData = field_value;
        } else if (field_name === 'frs') {
            //Special treatment for setting defaults for any FRS field in the tracker
            _.forOwn(extra_fields, function (extra_field_data, extra_field_name) {
                if (_.has(extra_field_data, 'fieldType') && extra_field_data.fieldType == 8) {
                    var field_element = _.find(extra_field_data.fieldElements, { id: field_value.id });

                    if (field_element) {
                        _ti_model.extraFields[extra_field_name].fieldData = _.cloneDeep(field_element);
                    }
                }
            });
        }
    }

    private processErrorLength(args, errDescription) {
        var err;
        if (!Array.isArray(args) || args.length < 2) {
            return errDescription;
        }

        err = this.errorMessages.length_args;
        err = err.replace('$1', args[0]);
        err = err.replace('$2', args[1]);

        return errDescription + err;
    }

    /**
     * Reduces a field-element value (a rich object carrying id, alias, label, and a dozen
     * mostly-null workflow properties) down to just its id -- or alias, for the rare element
     * identified only by alias. Scalars, null, '', and arrays (multiselect: reduced
     * element-wise) pass through as-is. The server's field mapping
     * (TrackerItemMappingService::GetValueFromField) only ever reads id/alias off these
     * objects; everything else is dead weight on the wire (a single tracker item's status
     * field alone carries its full workflow transition graph -- allowedRoles, notify,
     * llmPrompt, etc. -- for every possible next state).
     */
    private reduceFieldElement(value) {
        if (Array.isArray(value)) {
            return _.map(value, (v) => this.reduceFieldElement(v));
        }
        if (_.isObject(value)) {
            if (_.has(value, 'id')) {
                return _.pick(value, ['id']);
            }
            if (_.has(value, 'alias')) {
                return _.pick(value, ['alias']);
            }
        }
        return value;
    }

    /**
     * Given a full tracker item object, it returns a stripped down
     * version with just the data needed for submission
     *
     * @param {Object} ti
     * @returns
     */
    public stripForSave(ti) {
        var toSave = _.omitBy(
            ti,
            function (value, key) {
                if (['hasSubitems', 'sortOrder', 'subitemsCount', 'tracker'].includes(key)) {
                    return true;
                }
                if (key.startsWith('$') || typeof value == 'function') {
                    return true;
                }
                return false;
            }
        );

        toSave.rel = _.pick(_.get(toSave, 'rel', {}), ['assignees']);
        toSave.rel.assignees = _.map(
            _.get(toSave.rel, 'assignees', []),
            (assignee) => this.reduceFieldElement(assignee)
        );
        toSave.tracker = _.pick(_.get(ti, 'tracker', {}), ['id']);

        if (toSave.sprint) {
            toSave.sprint = _.pick(toSave.sprint, ['fieldData']);
            toSave.sprint.fieldData = this.reduceFieldElement(toSave.sprint.fieldData);
        }

        toSave.extraFields = _.reduce(
            toSave.extraFields,
            (carry, item, key) => {
                carry[key] = _.pick(item, ['fieldData']);
                carry[key].fieldData = this.reduceFieldElement(carry[key].fieldData);
                return carry;
            },
            {}
        );

        return toSave;
    }

    /**
     * 
     * @param {*} object 
     * @param {*} base 
     * @returns 
     */
    private difference(object, base) {
        function changes(object, base) {
            return _.transform(object, function (result: any, value, key: string) {
                let arrProperties: Array<string>;
                arrProperties = ['lastModifiedDate', 'id', 'tracker'];
                if (!_.isEqual(value, base[key]) || arrProperties.includes(key)) {
                    result[key] = (_.isObject(value) && _.isObject(base[key])) ? changes(value, base[key]) : value;
                }
            });
        }

        return changes(object, base);
    }

    private hasValidationFailure(response) {
        return (!_.has(response, 'id'));
    }

    private setLastModified(trackeritem, updated) {
        trackeritem.lastModifiedBy = _.get(updated, 'lastModifiedBy', trackeritem.lastModifiedBy);
        trackeritem.lastModifiedDate = _.get(updated, 'lastModifiedDate', trackeritem.lastModifiedDate);
        return trackeritem;
    }

    // END PRIVATE FUNCTIONS

    // START PUBLIC FUNCTIONS

    public getTrackeritem() {
        return this.trackeritem;
    }

    public setTrackeritem(_trackeritem) {
        this.trackeritem = _trackeritem;
    }

    public generateTempTid() {
        this.tempTid = _.random(1, 9999);
        return this.tempTid;
    }

    /**
     * Temp ref id used to upload files before the tracker item exists. Minted on
     * first use so callers that never went through the full Add page (the Add
     * modal, for one) still get a usable id instead of undefined.
     */
    public getTempTid() {
        if (!this.tempTid) {
            this.generateTempTid();
        }
        return this.tempTid;
    }

    public setTempTid(_tempTid) {
        this.tempTid = _tempTid;
    }

    public saveTi(ti, update) {
        var update_obj: any = _.defaults(
            {
                id: ti.id,
                lastModifiedDate: ti.lastModifiedDate
            },
            update
        );

        return new Promise((resolve, reject) => {
            this.trackeritemRest.save(ti.id, update_obj)
                .then((response: any) => {
                    if (this.hasValidationFailure(response)) {
                        reject(response);
                    } else {
                        // Update the path/value into that object.
                        _.assign(ti, update);
                        this.setLastModified(ti, response);
                        resolve(ti);

                    }
                })
                .catch((err) => {
                    reject(err);
                });
        });
    }

    public updateTi(ti, path, value) {
        var update_obj = _.set(
            {}, path, value
        );

        return this.saveTi(ti, update_obj);
    }

    /**
     * Sets the given status to the trackeritem and persists
     * @param {object} trackeritem
     * @param {object} status
     * @return {promise} Returns promise for the save (persist) operation
     */
    public setStatus(trackeritem, status) {
        return this.updateTi(
            trackeritem, 'extraFields.status.fieldData', status
        );
    }

    /**
     * Sets the given priority to the trackeritem and persists
     * @param {object} trackeritem
     * @param {int} priority
     * @return {promise} Returns promise for the save (persist) operation
     */
    public setPriority(trackeritem, priority) {
        return this.updateTi(
            trackeritem, 'priority', priority
        );
    }

    public allowUpdates(trackeritem) {
        // #44114 - If lockStatus is null, _.get won't substitute the default value
        var val = _.get(trackeritem, 'lockStatus', 0);
        return (val == null || val == 0);
    }

    /**
     * Sets the given assignee to the trackeritem and persists
     * @param {object} trackeritem
     * @param {object} assignee
     * @return {promise} Returns promise for the save (persist) operation
     */
    public setAssignee(trackeritem, assignee) {
        return this.updateTi(
            trackeritem, 'rel.assignees', [assignee]
        );
    }

    public deleteItems(idList: any) {
        idList = idList || [];
        return new Promise((resolve, reject) => {
            this.trackeritemRest.delete(0, idList.join(','))
                .then((response) => {
                    try {
                        ($('#delSelectedModal') as any).modal('hide');//jquery for modal
                    } catch (err) { }

                    this.notificationService.notifyAll("TRACKERITEM.MASS.DELETE.SUCCESS", response);
                    resolve(null);
                })
                .catch(() => {
                    try {
                        ($('#delSelectedModal') as any).modal('hide');//jquery for modal
                    } catch (err) { }

                    this.notificationService.notifyAll("TRACKERITEM.MASS.DELETE.ERROR", {});
                    reject(null);
                });
        });
    }

    public getTiUpdate(ti_data, options) {
        var ti_data_post;

        if (!ti_data) {
            return false;
        }

        var defaultOptions = {
            setAssigneeOnly: false
        };

        if (!options) {
            options = {};
        }
        options = _.defaults(options, defaultOptions);

        if (_.get(options, 'setAssigneeOnly', false)) {
            ti_data_post = {
                id: _.get(ti_data, 'id', 0),
                lastModifiedDate: ti_data?.lastModifiedDate
            };
        } else {
            ti_data_post = _.cloneDeep(ti_data);
        }

        //Set the temporary reference id for uploaded images, but only if there's a real one to
        //migrate -- omit rather than send a meaningless 0 (#65237 feedback).
        if (_.get(options, 'tmpRefId', 0)) {
            ti_data_post.tmpRefId = options.tmpRefId;
        }

        var assignees = _.get(ti_data, 'rel.assignees', []);

        // Clear the entire rel structure
        ti_data_post.rel = {};

        // Add the assignees back if any are set (even Nobody)
        // If not, then we will post without the rel, and let
        // the server validate for us.
        if (_.size(assignees) > 0) {
            ti_data_post.rel = {
                assignees: assignees
            };
        }

        if (_.has(ti_data, 'summary')) {
            ti_data_post.summary = _.get(ti_data, 'summary', '');
        }

        if (_.isEmpty(ti_data_post.waiting_for)) {
            delete ti_data_post.waiting_for;
        }

        var submitter = _.get(ti_data_post, 'submittedBy.id', _.get(ti_data_post, 'submittedBy', false));
        if (submitter) {
            ti_data_post.submittedBy = submitter;
        }

        var modifier = _.get(ti_data_post, 'lastModifiedBy.id', _.get(ti_data_post, 'lastModifiedBy', false));
        if (modifier) {
            ti_data_post.lastModifiedBy = modifier;
        }

        ti_data_post.statusId = _.get(ti_data, 'extraFields.status.fieldData.statusId', ti_data_post.statusId);

        // Only carry tags over if the source object actually had them -- distinguishes "never
        // set" (a brand-new item; omit) from "explicitly cleared" (an edited item's tags field
        // present but empty; still send so the clear takes effect). #65237 feedback: a new item
        // with no tags shouldn't submit tags: '' at all.
        if (_.has(ti_data, 'tags')) {
            ti_data_post.tags = ti_data.tags;
        }
        if (!["100", "1", "2"].includes(_.toString(ti_data_post.waitingFor))) {
            ti_data_post.waitingFor = 100;
        }

        // Empty open/close dates carry no information -- omit rather than send ''
        // (#65237 feedback; previously commented out here unconditionally, which would have
        // also stripped genuine date values on every save).
        if (ti_data_post.openDate === '') {
            delete ti_data_post.openDate;
        }
        if (ti_data_post.closeDate === '') {
            delete ti_data_post.closeDate;
        }
        if (ti_data_post.storyPoints === '') {
            delete ti_data_post.storyPoints;
        }

        return ti_data_post;
    }


    public setUnreleasedReleasesOnReleaseField(field: any) {
        return this.frsReleaseService.getUnreleased(this.stateService.params.unixName)
            .then((ret: any) => {
                field.fieldElements = _.map(ret.items, function (item) {
                    return {
                        id: item.id,
                        alias: item.releaseName,
                        label: item.releaseName,
                        statusId: 0,
                        defaultSelected: false,
                        sortOrder: 0
                    };
                });
            });
    }

    public setDefaultValueForReleaseField(trackeritem: any, releaseFieldConfig: any = null) {
        if (_.isEmpty(releaseFieldConfig) || releaseFieldConfig.attribute2 != 1 || _.isEmpty(releaseFieldConfig.defaultValue)) return;

        var releaseField = trackeritem.extraFields[releaseFieldConfig.alias];

        switch (releaseFieldConfig.defaultValue) {
            case "-1": //no default
                releaseField.fieldData = null;
                break;

            case "-2": //default: latest
                var elements = _.sortBy(_.filter(releaseFieldConfig.fieldElements, { statusId: 0 }), function (item) {
                    return item.id;
                });

                if (elements?.length > 0) {
                    _.set(releaseField, 'fieldData', elements[elements.length-1]);
                }
                break;

            default:
            case "-3": //default: specific release
                var release: any = _.find(releaseFieldConfig.fieldElements, function (item) {
                    return (item.id == releaseFieldConfig.defaultValue);
                });

                if (!_.isEmpty(release)) {
                    _.set(releaseField, 'fieldData', release);
                }
                break;
        }
    }

    public postEvent(trackerItemId: number, payload: object): Promise<any> {
        return this.trackeritemRest.postEvent(trackerItemId, payload);
    }

    public followupDelete(followupId, trackerItemId) {
        return this.trackeritemRest.deleteFollowup(trackerItemId, followupId)
            .then(() => {
                this.uiService.message.showSuccess("Follow-up deleted");
            }, (response) => {
                this.uiService.message.showError("There was an error trying to delete the follow up");
                throw response;
            });
    }

    public followupSave(strMessage, refId, trackerItemId, userId) {
        var params: any = {};
        params.body = strMessage;
        params.user = userId;
        params.refId = refId;

        return this.trackeritemRest.postFollowup(trackerItemId, 0, params)
            .then(() => {
                this.uiService.message.showSuccess("Follow up saved");
            }, (response) => {
                this.uiService.message.showError("There was an error trying to save the follow up");
                throw response;
            });
    }

    public tagsSave(tid, tags: Array<any>) {
        var strTags = '';
        var tags = _.map(tags, 'text');
        if (Array.isArray(tags) && tags.length > 0) {
            strTags = tags.join(',');
        }

        return this.tagsModel.save('trackeritem', tid, strTags)
            .then(() => {
                this.uiService.message.showSuccess("Tags successfully applied to tracker item  #" + tid, "Tracker item tagged");
            }, (response) => {
                this.uiService.message.showError("There was a problem saving tags for this tracker item.", "Tags Not Saved");
                throw response;
            });
    }

    public save(trackerItem, options) {
        var toSave = this.stripForSave(this.getTiUpdate(trackerItem, options));

        return this.trackeritemRest.save(trackerItem.id, toSave)
            .then((response: any) => {
                //Create the success message
                //Link here is not really needed as all notifications (growl and desktop) are clickable by item id
                this.uiService.message.showSuccess("Tracker Item <a href='/trackeritem/" + response.id + "'>#" + response.id + "</a> saved");
                return response;
            }, (response) => {
                throw this.processErrors(response.error);
            });
    }

    public move(trackerItem, toTrackerId, options) {
        // Set tracker to move to
        trackerItem.tracker_id = toTrackerId;
        var toSave = this.stripForSave(this.getTiUpdate(trackerItem, options));
        return this.trackeritemRest.move(trackerItem.id, toSave)
            .then((response: any) => {
                //Create the success message
                //Link here is not really needed as all notifications (growl and desktop) are clickable by item id
                this.uiService.message.showSuccess("Tracker Item <a href='/trackeritem/" + response.id + "'>#" + response.id + "</a> saved");
                return response;
            }, (response) => {
                throw this.processErrors(response.error);
            });
    }

    /**
     * Gets ACLs object from the given project and tracker id
     * @param {object} project
     * @param {int} tracker it
     * @return {object} ACLs
     */
    public getAcls(project, tracker_id, trackeritem) {
        var perms = _.get(project, 'rel.permissions.trackers[' + tracker_id + ']', {});

        var defaults = {
            canRead: false,
            canSubmit: false,
            canUpdate: false,
            canAssign: false,
            isAdmin: false
        };

        var ret: any = Object.keys(defaults).reduce(function (carry, item) {
            carry[item] = _.get(perms, item, false);
            return carry;
        }, defaults);

        //Default permission for adding followups
        //is identical to canUpdate
        ret.canFollowup = ret.canUpdate;

        //Update canUpdate and canFollowp according to the lock status
        if (trackeritem.lockStatus == 1) {
            ret.canFollowup = false;
            ret.canUpdate = false;
        } else if (trackeritem.lockStatus == 2) {
            ret.canFollowup = ret.canFollowup;
            ret.canUpdate = false;
        }

        return ret;
    }

    /**
     * Loads a tracker by id and returns a blank trackeritem model for it in one step.
     * Extracted from TrackeritemAddModalComponent.changeTracker() (#65237) so the Breakdown
     * tab's Field Values panel can reuse the same tracker-switch behavior -- deliberately
     * excludes the Add modal's "remember last tracker" user-preference side effect, which is
     * specific to that modal's UX and stays there.
     *
     * Note: the original changeTracker() called getBlankModel(tracker, tracker.fields) --
     * passing the *whole* tracker.fields config dict (intrinsic and extra fields alike) as
     * getBlankModel's `extra_fields` param. That param exists to "reinject" an already-shaped
     * extraFields dict (see getBlankModel's own comment), not raw tracker field config; passing
     * tracker.fields there took the `!_.isEmpty(extra_fields)` branch, which assigns it
     * directly, aliasing intrinsic fields (priority, openDate, ...) into extraFields verbatim
     * and skipping the real per-field default/fieldData construction. Fixed here (single-arg
     * call, correct branch) since the bug affects both this method's callers.
     * @param trackerId
     * @returns {Promise<{tracker: any, trackeritem: any}>}
     */
    public loadTrackerAndBlankModel(trackerId: any): Promise<{ tracker: any, trackeritem: any }> {
        return this.trackerService.load(trackerId)
            .then((tracker: any) => {
                if (!tracker) throw new Error('Tracker data is undefined');
                return {
                    tracker: tracker,
                    trackeritem: this.getBlankModel(tracker)
                };
            });
    }

    public getBlankModel(tracker_data, extra_fields = null, defaults = null) {
        var _ti_model = {
            tracker: {
                id: tracker_data.id
            },
            rel: { assignees: [this.userService.getNobody()] },
            summary: "",
            priority: this.trackerService.fields.builtIn.priority.default,
            details: "",
            waiting_for: "",
            openDate: "",
            closeDate: "",
            sprint: {
                alias: "sprint",
                fieldName: "Sprint",
                fieldData: {
                    id: 100,
                    label: 'None',
                    alias: 'none'
                }
            },
            storyPoints: "",
            extraFields: {}
        };

        //Set story points default, if applicable
        if (_.get(tracker_data, 'fields.storyPoints.isEnabled', false)) {
            var storyPointsDefault = tracker_data?.storyPointsList?.default ?? null;
            var storyPointsList = _.get(tracker_data, "storyPointsList.list", []);
            if (_.includes(storyPointsList, storyPointsDefault)) {
                _ti_model.storyPoints = storyPointsDefault;
            }
        }

        //If the extra_fields params was passed, don't reset extra fields,
        //instead, reinject them
        if (!_.isEmpty(extra_fields)) {
            _ti_model.extraFields = extra_fields;
        } else {
            // getBlankModel() only ever builds a template for a *new* item (id 0), so this
            // matches the "showOnSubmit || trackeritem.id > 0" gating used everywhere a field
            // is rendered (e.g. trackeritem.fields.component.html) -- a field the tracker admin
            // marked as not shown on submit shouldn't be populated (even with just a default)
            // for a brand-new item either.
            _.forEach(tracker_data.fields, (value, key) => {
                if (value.isExtraField == true && value.isEnabled == true && value.showOnSubmit == true) {
                    var defaultElement = _.find(
                        _.get(value, 'fieldElements', []),
                        { defaultSelected: true }
                    );

                    _ti_model.extraFields[key] = {
                        fieldName: value.fieldName,
                        alias: value.alias,
                        fieldType: value.fieldType,
                        attribute1: value.attribute1,
                        attribute2: value.attribute2
                    };

                    var defaultValue: any = defaultElement || ((value.fieldType != 8) ? value.defaultValue : '') || '';
                    _ti_model.extraFields[key].fieldData = (value.fieldType == 2 || value.fieldType == 5) ? [defaultValue] : defaultValue;
                }
            });
        }

        //Set specials defaults (other than the default provided by the tracker)
        if (!_.isEmpty(defaults)) {
            _.forOwn(defaults, function (field_data, field_name) {
                if (field_name != 'extraFields') {
                    _ti_model[field_name] = field_data;
                } else {
                    _.forOwn(field_data, function (extra_field_data, extra_field_name) {
                        this.setDefaultForExtraField(_ti_model, tracker_data.fields, extra_field_name, extra_field_data);
                    });
                }
            });
        }

        return _ti_model;
    }

    public getSection() {
        return 'trackeritem'
    }

    public getSectionTmp() {
        return '_trackeritem'
    }

    public getFollowupSection() {
        return 'trackeritem_message'
    }

    public getFollowupSectionTmp() {
        return '_trackeritem_message'
    }

    public processErrors(errors_list) {
        if (_.has(errors_list, 'blocked')) {
            return errors_list;
        }

        if (_.has(errors_list, 'overwrite')) {
            errors_list = errors_list.fields;
        }

        var errDescription;
        var errors = {};

        _.forEach(errors_list, (value, key) => {
            var error_type = _.find(value, function (item) {
                if (_.has(item, 'type')) {
                    return true;
                }
            });

            if (error_type === undefined) {
                error_type = {
                    type: ''
                };
            }

            errDescription = error_type.type;

            //Try to get proper description for the error
            if (_.has(this.errorMessages, error_type.type)) {
                errDescription = this.errorMessages[error_type.type];
            }

            if (!_.isEmpty(error_type.args)) {
                switch (error_type.type) {
                    case "length":
                        errDescription = this.processErrorLength(error_type.args, errDescription);
                        break;
                    case "approval-required":
                        errDescription = 'This transition requires an approval before saving.';
                        break;
                }
            }

            errors[key] = {
                hasError: true,
                error: errDescription
            };
        });

        return errors;
    }

    public fetch(trackerItemId, options) {
        if (options === undefined) {
            options = {
                fetchFull: false
            };
        }

        if (!_.has(options, 'show_error_messages')) {
            options.show_messages = false;
        }


        //Success callback
        function _onFetchSuccess(data) {
            return data;
        }

        //Error callback
        function _onFetchError(error) {
            if (options.show_error_messages) {
                this.UiService.message.showError("There was a problem loading the selected tracker item.", "Tracker Item Not Found");
            }

            //Set tracker item not found error
            if (error.status == 404) {
                error.data = "The task doesn't exist or was deleted";
            }

            return (error);
        }

        var promise;
        if (options.fetchFull) {
            promise = this.trackeritemRest.getDetails(trackerItemId, { rel: 'attachments,assignees,field_elements,merge_status' });
        } else {
            promise = this.trackeritemRest.fetch(trackerItemId);
        }

        return promise
            .then(_onFetchSuccess, _onFetchError);
    }

    public fetchTimeline(id) {
        return this.trackeritemRest.getDetails(id, { rel: 'history,messages,merge_comments,commits,associations,dependencies,duplicates,events' });
    }

    /**
     * Set attachment for the tracker item.
     * @param trackeritemId The ID of the tracker item
     * @param hash The file hash
     * @param params Additional parameters for the attachment
     * @returns A promise with the set attachment result
     */
    setAttachment(trackeritemId: number, hash: any, params: any): Promise<any> {
        return this.trackeritemRest.postAttachment(trackeritemId, hash, params);
    }

    /**
     * Returns true if the given attachment object represents a markdown file,
     * based on its MIME type or file extension.
     */
    fileIsMarkdown(attachment: any): boolean {
        const type = typeof attachment.fileType === 'string' ? attachment.fileType.toLowerCase() : '';
        const name = typeof attachment.fileName === 'string' ? attachment.fileName.toLowerCase() : '';
        return type.startsWith('text/markdown')
            || type.startsWith('text/x-markdown')
            || name.endsWith('.md')
            || name.endsWith('.markdown');
    }

    private getbranches(trackerItemId, options: any = {}) {
        if (!_.has(options, 'show_error_messages')) {
            options.show_messages = false;
        }

        return new Promise((resolve, reject) => {
            this.trackeritemRest.getBranches(trackerItemId)
                .then((response) => {
                    resolve(response);
                })
                .catch((err) => {
                    if (options.show_error_messages) {
                        this.uiService.message.showError("There was a problem finding work branches for this Tracker Item.", "Branches Not Found");
                    }

                    //Set tracker item not found error
                    if (err.status == 404) {
                        err.data = "The Tracker Item doesn't exist or was deleted";
                    }

                    reject(err);
                });
        });
    }

    public getTrackeritemMerge(trackeritem) {
        var tiMerge: any = {
            branches: false,
            promotionBranches: false,
            fromBranch: '',
            toBranch: '',
            toBranchCustom: '',
            isMerge: false,
            merge: false,
            loading: false,
            merging: false,
            view: 'sbs'
        };

        return this.getbranches(trackeritem.id)
            .then((result: any) => {
                tiMerge.branches = result.items;
                // Set default item
                tiMerge.fromBranch = _.get(tiMerge, 'branches[0]', '');
                tiMerge.isMerge = (tiMerge.branches.length > 0);

                tiMerge.promotionBranches = _.keys(_.get(trackeritem, 'rel.merge_status', {})).filter(function (item) {
                    if (item != "") return true;
                    return false;
                });
                tiMerge.toBranch = _.first(tiMerge.promotionBranches);
                trackeritem.rel.commits = _.sortBy(_.get(trackeritem, 'rel.commits', []), ['id']).reverse();
                tiMerge.selectedCommit = _.first(trackeritem.rel.commits);

                return tiMerge
            });
    }

    /**
     * Return true if the given ti is assigned to the given member, false otherwise
     * @param {object} tracker item
     * @param {object} assignee
     * @returns {boolean}
     */
    public isAssignedTo(trackeritem, assignee) {
        return _.findIndex(
            _.get(trackeritem, 'rel.assignees', []),
            { id: _.get(assignee, 'id', -1) }
        ) > -1;
    }

    /**
     * Updates tracker item values, preserving original object references
     * @param original The original tracker item
     * @param updated The updated tracker item
     * @returns The merged tracker item
     */
    public updateTIValues(original, updated) {
        const result = _.merge({}, original, updated);
        result.rel = _.merge({}, original.rel, updated.rel);
        
        // For extraFields, preserve original field object references for UI binding
        const originalExtraFields = original.extraFields || {};
        const updatedExtraFields = _.get(updated, 'extraFields', {});

        result.extraFields = Object.assign({}, originalExtraFields);

        _.forEach(updatedExtraFields, (updatedField, alias) => {
            if (result.extraFields[alias] && updatedField) {
                if (_.has(updatedField, 'fieldData')) {
                    result.extraFields[alias].fieldData = updatedField.fieldData;
                }
                if (_.has(updatedField, 'fieldName')) {
                    result.extraFields[alias].fieldName = updatedField.fieldName;
                }
                if (_.has(updatedField, 'fieldType')) {
                    result.extraFields[alias].fieldType = updatedField.fieldType;
                }
                if (_.has(updatedField, 'attribute1')) {
                    result.extraFields[alias].attribute1 = updatedField.attribute1;
                }
                if (_.has(updatedField, 'attribute2')) {
                    result.extraFields[alias].attribute2 = updatedField.attribute2;
                }
                if (_.has(updatedField, 'fieldElements')) {
                    result.extraFields[alias].fieldElements = updatedField.fieldElements;
                }
            } else if (!result.extraFields[alias] && updatedField) {
                // If the field doesn't exist in original trackeritem data, add it
                result.extraFields[alias] = _.cloneDeep(updatedField);
            }
        });

        return result;
    }
}
